import { router, usePage } from "@inertiajs/react"
import { useEffect, useRef } from "react"
import { toast } from "sonner"

import {
  confirmEmailVerificationMethodSettingsSecurityPath,
  settingsSecurityPath,
} from "@/routes"
import type { User } from "@/types"

type ReminderUser = Pick<
  User,
  | "id"
  | "disclaimer_accepted_at"
  | "mobile_verified_at"
  | "two_factor_delivery_method"
  | "two_factor_preference_confirmed_at"
>

interface SmsTwoFactorReminderPageProps {
  [key: string]: unknown
  auth?: {
    user?: ReminderUser | null
    shared_access?: boolean
  }
}

const TOAST_ID_PREFIX = "sms-2fa-reminder"
const REMINDER_DELAY_MS = 3_000
const REMINDER_DURATION = Number.POSITIVE_INFINITY

function pagePath(url: string) {
  return url.split("?")[0].split("#")[0]
}

export function SmsTwoFactorReminder() {
  const page = usePage<SmsTwoFactorReminderPageProps>()
  const user = page.props.auth?.user ?? null
  const isSharedAccess = Boolean(page.props.auth?.shared_access)
  const activeToastIdRef = useRef<string | null>(null)
  const userId = user?.id ?? null

  const hasVerifiedSms =
    user?.two_factor_delivery_method === "sms" &&
    Boolean(user.mobile_verified_at)
  const hasConfirmedPreference = Boolean(
    user?.two_factor_preference_confirmed_at,
  )
  const isSecuritySettingsPage = pagePath(page.url) === settingsSecurityPath()
  const shouldShowReminder = Boolean(
    user &&
      !isSharedAccess &&
      user.disclaimer_accepted_at &&
      !hasVerifiedSms &&
      !hasConfirmedPreference &&
      !isSecuritySettingsPage,
  )

  useEffect(() => {
    if (!userId || !shouldShowReminder) {
      if (activeToastIdRef.current) {
        toast.dismiss(activeToastIdRef.current)
        activeToastIdRef.current = null
      }
      return
    }

    const toastId = `${TOAST_ID_PREFIX}-${userId}`

    const handleEmailPreference = () => {
      router.patch(
        confirmEmailVerificationMethodSettingsSecurityPath(),
        {},
        {
          preserveScroll: true,
          onSuccess: () => {
            toast.dismiss(toastId)
            activeToastIdRef.current = null
          },
          onError: () => {
            toast.error("We couldn't save your verification preference.")
          },
        },
      )
    }

    const handleSmsPreference = () => {
      toast.dismiss(toastId)
      activeToastIdRef.current = null
      router.visit(settingsSecurityPath())
    }

    const timeoutId = window.setTimeout(() => {
      activeToastIdRef.current = toastId

      toast("Choose your verification method", {
        id: toastId,
        description: "How would you prefer to receive sign-in codes?",
        duration: REMINDER_DURATION,
        action: {
          label: "SMS",
          onClick: handleSmsPreference,
        },
        cancel: {
          label: "Email",
          onClick: handleEmailPreference,
        },
      })
    }, REMINDER_DELAY_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [shouldShowReminder, userId])

  return null
}
